/**
 * BrightPath - ExpenseFilters Component
 * BP-04-04: Expenses Frontend
 */

import React, { useState } from 'react';
import {
  Filter,
  X,
  ChevronDown,
  Calendar,
  DollarSign,
  Tag,
  User,
  Search,
  RotateCcw,
} from 'lucide-react';
import type { ExpenseFilters as IExpenseFilters, ExpenseCategory } from '../types';
import { EXPENSE_CATEGORIES } from '../types';
import { useDateRangePresets } from '../hooks/useExpenseFilters';
import { cn } from '@/core/lib/utils';

interface ExpenseFiltersProps {
  filters: IExpenseFilters;
  onFilterChange: <K extends keyof IExpenseFilters>(key: K, value: IExpenseFilters[K]) => void;
  onFiltersChange: (filters: Partial<IExpenseFilters>) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  children?: { id: string; firstName: string; lastName: string }[];
  availableTags?: string[];
  className?: string;
}

export function ExpenseFilters({
  filters,
  onFilterChange,
  onFiltersChange,
  onClearFilters,
  hasActiveFilters,
  activeFilterCount,
  children = [],
  availableTags = [],
  className,
}: ExpenseFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDatePresets, setShowDatePresets] = useState(false);
  const datePresets = useDateRangePresets();

  const handleDatePreset = (preset: { startDate: string; endDate: string }) => {
    onFiltersChange({
      startDate: preset.startDate,
      endDate: preset.endDate,
    });
    setShowDatePresets(false);
  };

  const handleClearDateRange = () => {
    onFiltersChange({
      startDate: null,
      endDate: null,
    });
  };

  return (
    <div className={cn('bg-white rounded-xl border border-warm-gray/10', className)}>
      {/* Quick Search and Filter Toggle */}
      <div className="p-4 flex items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
          <input
            type="text"
            value={filters.search || ''}
            onChange={(e) => onFilterChange('search', e.target.value || null)}
            placeholder="Search expenses..."
            className="w-full pl-12 pr-4 py-2.5 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
          />
          {filters.search && (
            <button
              onClick={() => onFilterChange('search', null)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-cream-dark rounded transition-colors"
            >
              <X className="w-4 h-4 text-warm-gray" />
            </button>
          )}
        </div>

        {/* Category Quick Filter */}
        <select
          value={filters.category || ''}
          onChange={(e) => onFilterChange('category', (e.target.value as ExpenseCategory) || null)}
          className="px-4 py-2.5 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors min-w-[160px]"
        >
          <option value="">All Categories</option>
          {(Object.entries(EXPENSE_CATEGORIES) as [ExpenseCategory, typeof EXPENSE_CATEGORIES[ExpenseCategory]][]).map(
            ([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            )
          )}
        </select>

        {/* Expand Filters Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-colors',
            isExpanded || hasActiveFilters
              ? 'border-sage bg-sage/10 text-sage-dark'
              : 'border-warm-gray/20 hover:border-sage'
          )}
        >
          <Filter className="w-5 h-5" />
          <span className="font-medium">Filters</span>
          {activeFilterCount > 0 && (
            <span className="w-5 h-5 bg-sage text-white rounded-full text-xs flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            className={cn('w-4 h-4 transition-transform', isExpanded && 'rotate-180')}
          />
        </button>

        {/* Clear All */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-2 px-3 py-2 text-warm-gray hover:text-navy transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="text-sm">Clear</span>
          </button>
        )}
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-warm-gray/10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-navy mb-2">Date Range</label>
              <div className="relative">
                <button
                  onClick={() => setShowDatePresets(!showDatePresets)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-cream rounded-lg text-left hover:bg-cream-dark transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-warm-gray" />
                    {filters.startDate && filters.endDate ? (
                      <span className="text-navy text-sm">
                        {new Date(filters.startDate).toLocaleDateString()} -{' '}
                        {new Date(filters.endDate).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-warm-gray text-sm">Select dates</span>
                    )}
                  </span>
                  <ChevronDown className="w-4 h-4 text-warm-gray" />
                </button>

                {showDatePresets && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowDatePresets(false)}
                    />
                    <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-warm-gray/10 py-1 z-20">
                      {datePresets.map((preset) => (
                        <button
                          key={preset.label}
                          onClick={() => handleDatePreset(preset)}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-cream transition-colors"
                        >
                          {preset.label}
                        </button>
                      ))}
                      <div className="border-t border-warm-gray/10 mt-1 pt-1">
                        <button
                          onClick={() => {
                            setShowDatePresets(false);
                            // Open custom date picker (you'd implement a modal here)
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-sage hover:bg-cream transition-colors"
                        >
                          Custom Range...
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {(filters.startDate || filters.endDate) && (
                  <button
                    onClick={handleClearDateRange}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-cream-dark rounded"
                  >
                    <X className="w-4 h-4 text-warm-gray" />
                  </button>
                )}
              </div>
            </div>

            {/* Amount Range */}
            <div>
              <label className="block text-sm font-medium text-navy mb-2">Amount Range</label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={filters.minAmount || ''}
                    onChange={(e) =>
                      onFilterChange('minAmount', e.target.value ? parseFloat(e.target.value) : null)
                    }
                    placeholder="Min"
                    className="w-full pl-9 pr-3 py-2.5 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors text-sm"
                  />
                </div>
                <span className="text-warm-gray">-</span>
                <div className="relative flex-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={filters.maxAmount || ''}
                    onChange={(e) =>
                      onFilterChange('maxAmount', e.target.value ? parseFloat(e.target.value) : null)
                    }
                    placeholder="Max"
                    className="w-full pl-9 pr-3 py-2.5 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Child Filter */}
            {children.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Child</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                  <select
                    value={filters.childId || ''}
                    onChange={(e) => onFilterChange('childId', e.target.value || null)}
                    className="w-full pl-10 pr-4 py-2.5 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors appearance-none"
                  >
                    <option value="">All Children</option>
                    {children.map((child) => (
                      <option key={child.id} value={child.id}>
                        {child.firstName} {child.lastName}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray pointer-events-none" />
                </div>
              </div>
            )}

            {/* Vendor Filter */}
            <div>
              <label className="block text-sm font-medium text-navy mb-2">Vendor</label>
              <input
                type="text"
                value={filters.vendorName || ''}
                onChange={(e) => onFilterChange('vendorName', e.target.value || null)}
                placeholder="Search vendors..."
                className="w-full px-4 py-2.5 bg-cream rounded-lg border-2 border-transparent focus:border-sage focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* Toggle Filters */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-warm-gray/10">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.isTaxDeductible === true}
                onChange={(e) =>
                  onFilterChange('isTaxDeductible', e.target.checked ? true : null)
                }
                className="w-4 h-4 rounded border-warm-gray/30 text-sage focus:ring-sage"
              />
              <span className="text-sm text-navy">Tax Deductible Only</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.isInsuranceCovered === true}
                onChange={(e) =>
                  onFilterChange('isInsuranceCovered', e.target.checked ? true : null)
                }
                className="w-4 h-4 rounded border-warm-gray/30 text-sage focus:ring-sage"
              />
              <span className="text-sm text-navy">Insurance Covered</span>
            </label>
          </div>

          {/* Tags Filter */}
          {availableTags.length > 0 && (
            <div className="mt-4 pt-4 border-t border-warm-gray/10">
              <label className="block text-sm font-medium text-navy mb-2">Tags</label>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => {
                  const isSelected = filters.tags?.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => {
                        const newTags = isSelected
                          ? (filters.tags || []).filter((t) => t !== tag)
                          : [...(filters.tags || []), tag];
                        onFilterChange('tags', newTags.length > 0 ? newTags : []);
                      }}
                      className={cn(
                        'px-3 py-1 rounded-full text-sm transition-colors',
                        isSelected
                          ? 'bg-sage text-white'
                          : 'bg-cream text-navy hover:bg-cream-dark'
                      )}
                    >
                      <Tag className="w-3 h-3 inline mr-1" />
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && !isExpanded && (
        <div className="px-4 pb-4 flex flex-wrap items-center gap-2">
          {filters.category && (
            <FilterChip
              label={EXPENSE_CATEGORIES[filters.category].label}
              onRemove={() => onFilterChange('category', null)}
            />
          )}
          {(filters.startDate || filters.endDate) && (
            <FilterChip
              label={`${filters.startDate || '...'} - ${filters.endDate || '...'}`}
              onRemove={handleClearDateRange}
            />
          )}
          {filters.childId && (
            <FilterChip
              label={children.find((c) => c.id === filters.childId)?.firstName || 'Child'}
              onRemove={() => onFilterChange('childId', null)}
            />
          )}
          {filters.isTaxDeductible && (
            <FilterChip
              label="Tax Deductible"
              onRemove={() => onFilterChange('isTaxDeductible', null)}
            />
          )}
          {filters.isInsuranceCovered && (
            <FilterChip
              label="Insurance Covered"
              onRemove={() => onFilterChange('isInsuranceCovered', null)}
            />
          )}
          {(filters.minAmount || filters.maxAmount) && (
            <FilterChip
              label={`$${filters.minAmount || 0} - $${filters.maxAmount || '∞'}`}
              onRemove={() => onFiltersChange({ minAmount: null, maxAmount: null })}
            />
          )}
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 bg-sage/10 text-sage-dark rounded-full text-sm">
      {label}
      <button onClick={onRemove} className="hover:text-sage">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

export default ExpenseFilters;
